import React, { useState, useEffect, useRef } from 'react';
import { HashRouter as Router } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Screensaver from './components/Screensaver';
import Settings from './components/Settings';
import LanguageMenu from './components/LanguageMenu';
import Guide from './components/Guide';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { translations } from './translations';
import './App.css';

const { FiSettings, FiPlay, FiMonitor, FiHelpCircle } = FiIcons;

function App() {
  const [isActive, setIsActive] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [language, setLanguage] = useState('fr');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [isKioskMode, setIsKioskMode] = useState(false);
  
  const [settings, setSettings] = useState({
    theme: 'christmas',
    snowflakeCount: 100,
    animationSpeed: 1,
    showClock: true,
    showGreeting: true,
    particleSize: 'medium',
    backgroundColor: 'night'
  });

  const t = translations[language];
  const timerRef = useRef(null);

  // Auto-start logic (Kiosk Mode)
  useEffect(() => {
    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (isKioskMode && !isActive && !showSettings && !showGuide) {
        timerRef.current = setTimeout(() => {
          setIsActive(true);
        }, 30000); // 30 seconds
      }
    };

    const handleActivity = () => {
      if (isActive) {
        setIsActive(false);
      }
      resetTimer();
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    if (isKioskMode || isActive) {
      events.forEach(event => {
        document.addEventListener(event, handleActivity, true);
      });
    }

    resetTimer();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [isActive, showSettings, isKioskMode, showGuide]);

  const handleSettingsUpdate = (newSettings) => {
    setSettings(newSettings);
  };

  const startCountdown = () => {
    let count = 3;
    setCountdown(count);
    const interval = setInterval(() => {
      count--;
      if (count === 0) {
        clearInterval(interval);
        setCountdown(null);
        setIsActive(true);
      } else {
        setCountdown(count);
      }
    }, 1000);
  };

  const toggleKioskMode = async () => {
    if (!document.fullscreenElement) {
      try {
        await document.documentElement.requestFullscreen();
        setIsKioskMode(true);
      } catch (e) {
        console.error("Fullscreen denied", e);
        setIsKioskMode(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsKioskMode(false);
      }
    }
  };

  return (
    <Router>
      <div className="min-h-screen bg-[#0f0c29] text-white overflow-hidden relative font-sans selection:bg-pink-500 selection:text-white">
        
        {/* Background Mesh Gradient */}
        <div className="fixed inset-0 z-0">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-purple-900/30 rounded-full blur-[120px]" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-blue-900/30 rounded-full blur-[120px]" />
          <div className="absolute top-[40%] left-[40%] w-[30%] h-[30%] bg-pink-900/20 rounded-full blur-[100px]" />
        </div>

        <AnimatePresence>
          {isActive && !showSettings && (
            <Screensaver 
              settings={settings} 
              onExit={() => setIsActive(false)} 
              uploadedImage={uploadedImage}
              language={language}
            />
          )}
        </AnimatePresence>

        {/* Countdown Overlay */}
        <AnimatePresence>
          {countdown !== null && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm"
            >
              <motion.div
                key={countdown}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1.5, opacity: 1 }}
                exit={{ scale: 2, opacity: 0 }}
                className="text-9xl font-bold text-white gradient-text"
              >
                {countdown}
              </motion.div>
              <p className="mt-8 text-white/60 text-xl font-light">{t.launching}...</p>
            </motion.div>
          )}
        </AnimatePresence>

        {!isActive && (
          <div className="min-h-screen flex flex-col items-center justify-center p-8 relative z-10">
            
            {/* Top Bar */}
            <div className="absolute top-0 left-0 w-full p-6 md:p-8 flex justify-between items-center z-50">
              <div className="flex flex-col">
                <span className="text-white/40 text-[10px] md:text-xs tracking-[0.2em] font-bold uppercase mb-1">Tibo Consultant</span>
                <span className="text-white/90 font-bold text-lg md:text-xl tracking-tight">
                  Holiday<span className="text-pink-500">Saver</span>
                </span>
              </div>
              <div className="flex items-center gap-3">
                <LanguageMenu language={language} onLanguageChange={setLanguage} />
              </div>
            </div>

            {/* Main Dashboard */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="w-full max-w-6xl mt-12 md:mt-0"
            >
              <div className="text-center mb-12 md:mb-16 relative">
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <h1 className="text-5xl md:text-7xl lg:text-8xl font-black mb-6 tracking-tight leading-tight drop-shadow-2xl">
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-200 via-white to-pink-200">
                      {t.title}
                    </span>
                  </h1>
                  <p className="text-lg md:text-2xl text-blue-100/60 font-light max-w-2xl mx-auto leading-relaxed mb-8">
                    {t.subtitle}
                  </p>

                  {/* Instructions Button - Centered below subtitle */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowGuide(true)}
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-full text-base font-medium backdrop-blur-md border border-white/10 transition-all shadow-lg hover:shadow-white/5"
                  >
                    <SafeIcon icon={FiHelpCircle} className="text-xl text-pink-300" />
                    <span>{t.howToUse}</span>
                  </motion.button>
                </motion.div>

                {/* Status Indicator */}
                <div className="mt-8 flex justify-center">
                  <div className={`px-5 py-2.5 rounded-full border flex items-center gap-2 ${
                    isKioskMode 
                      ? 'border-green-500/30 bg-green-500/10 text-green-300 shadow-lg shadow-green-900/20' 
                      : 'border-white/10 bg-white/5 text-white/40'
                    } text-sm font-medium backdrop-blur-md transition-all`}>
                    <span className={`w-2 h-2 rounded-full ${isKioskMode ? 'bg-green-400 animate-pulse' : 'bg-white/20'}`} />
                    {isKioskMode ? t.deployScreensaver : t.startScreensaver}
                  </div>
                </div>
              </div>

              {/* Action Cards - Reordered */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 px-4 md:px-0">
                
                {/* Position 1: Settings (Personalization) - Moved to First */}
                <motion.button
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowSettings(true)}
                  className="group relative h-72 rounded-[2rem] overflow-hidden glass-panel p-8 text-left transition-all hover:border-purple-500/50 hover:shadow-2xl hover:shadow-purple-500/10"
                >
                  <div className="absolute top-0 right-0 p-32 bg-purple-500/20 blur-[60px] rounded-full group-hover:bg-purple-500/30 transition-all duration-500" />
                  <div className="relative z-10 flex flex-col h-full justify-between">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-lg shadow-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                      <SafeIcon icon={FiSettings} className="text-2xl text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-3">{t.settings}</h3>
                      <p className="text-white/50 text-sm leading-relaxed">
                        {t.guide.step2Desc}
                      </p>
                    </div>
                  </div>
                </motion.button>

                {/* Position 2: Start Now - Moved to Second */}
                <motion.button
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={startCountdown}
                  className="group relative h-72 rounded-[2rem] overflow-hidden glass-panel p-8 text-left transition-all hover:border-pink-500/50 hover:shadow-2xl hover:shadow-pink-500/10"
                >
                  <div className="absolute top-0 right-0 p-32 bg-pink-500/20 blur-[60px] rounded-full group-hover:bg-pink-500/30 transition-all duration-500" />
                  <div className="relative z-10 flex flex-col h-full justify-between">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center shadow-lg shadow-pink-500/30 group-hover:scale-110 transition-transform duration-300">
                      <SafeIcon icon={FiPlay} className="text-2xl text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-3">{t.startScreensaver}</h3>
                      <p className="text-white/50 text-sm leading-relaxed">
                        {t.guide.step1Desc}
                      </p>
                    </div>
                  </div>
                  {/* Hover Arrow */}
                  <div className="absolute bottom-8 right-8 opacity-0 group-hover:opacity-100 transform translate-x-4 group-hover:translate-x-0 transition-all duration-300">
                     <span className="text-2xl">➔</span>
                  </div>
                </motion.button>

                {/* Position 3: Kiosk Mode - Moved to Third */}
                <motion.button
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={toggleKioskMode}
                  className={`group relative h-72 rounded-[2rem] overflow-hidden glass-panel p-8 text-left transition-all hover:shadow-2xl ${
                    isKioskMode 
                    ? 'border-green-500/50 bg-green-900/10 hover:shadow-green-500/10' 
                    : 'hover:border-blue-500/50 hover:shadow-blue-500/10'
                  }`}
                >
                  <div className={`absolute top-0 right-0 p-32 ${isKioskMode ? 'bg-green-500/20' : 'bg-blue-500/20'} blur-[60px] rounded-full transition-all duration-500`} />
                  <div className="relative z-10 flex flex-col h-full justify-between">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${
                      isKioskMode ? 'from-green-500 to-emerald-600' : 'from-blue-500 to-indigo-600'
                    } flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300`}>
                      <SafeIcon icon={FiMonitor} className="text-2xl text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-3">{t.deployScreensaver}</h3>
                      <p className="text-white/50 text-sm leading-relaxed">
                        {isKioskMode ? 'Mode actif. Lancement auto après 30s.' : t.autoStartInfo}
                      </p>
                    </div>
                  </div>
                </motion.button>

              </div>
            </motion.div>

            {/* Modals */}
            <AnimatePresence>
              {showSettings && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
                  <Settings 
                    settings={settings} 
                    onUpdate={handleSettingsUpdate} 
                    onClose={() => setShowSettings(false)}
                    language={language}
                    uploadedImage={uploadedImage}
                    onImageUpload={setUploadedImage}
                  />
                </div>
              )}
              {showGuide && (
                <Guide 
                  isOpen={showGuide}
                  onClose={() => setShowGuide(false)}
                  language={language}
                />
              )}
            </AnimatePresence>

            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="absolute bottom-8 text-white/30 text-xs font-medium tracking-widest uppercase"
            >
              {t.madeBy}{' '}
              <a 
                href="https://tiboconsultant.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white/60 hover:text-white transition-colors border-b border-transparent hover:border-white/60 pb-0.5"
              >
                Tibo Consultant
              </a>
            </motion.div>
          </div>
        )}
      </div>
    </Router>
  );
}

export default App;